// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { ProductVersion, VDCOSChartToolType, HexTriplet } from '@evolvemep/api-ts';
import type { AgChartOptions, AgSeriesTooltipRendererParams } from 'ag-charts-enterprise';

import { createColorScheme } from 'helpers/createColorScheme';
import { strongEntries } from 'helpers/strongEntries';
import { ChartRenderer } from 'modules/Shop/Insights/RevitUsage/components/ChartRenderer';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';
import { useChartFilters } from 'modules/Shop/Insights/RevitUsage/useChartFilters';
import { useAnalyticsChart } from 'modules/Shop/Insights/RevitUsage/views/charts/useAnalyticsChart';

export type InstalledVersionsData = {
  count: number;
  emails: string[];
  // oxlint-disable-next-line typescript/no-duplicate-type-constituents
  version: ProductVersion['productVersion'] | ProductVersion['revitVersion'];
};

const aggregateData =
  (toolType: VDCOSChartToolType) =>
  (productVersions: ProductVersion[]): InstalledVersionsData[] =>
    strongEntries(
      productVersions.reduce(
        (out, { email, productVersion, revitVersion }) => {
          const value = toolType === 'Revit' ? revitVersion : productVersion;
          if (out[value]) {
            if (!out[value].includes(email)) {
              out[value].push(email);
            }
          } else {
            out[value] = [email];
          }
          return out;
        },
        // oxlint-disable-next-line typescript/no-duplicate-type-constituents
        {} as Record<ProductVersion['productVersion'] | ProductVersion['revitVersion'], string[]>,
      ),
    )
      .map(([value, emails]) => ({ version: value, emails, count: emails.length }))
      .toSorted((a, b) => b.version.localeCompare(a.version));

export const InstalledEvolveVersionsChart = (
  props: AnalyticsChartProps<InstalledVersionsData[]> & {
    installedVersionsFills?: HexTriplet[];
  },
) => {
  const { toolType } = useChartFilters();
  const { selectedRevitYear } = useChartFilters();
  const [data, loading] = useAnalyticsChart(
    'usage-stats/v1/reporting/versions/product',
    props,
    aggregateData(toolType),
  );

  const { showDemo = false, installedVersionsFills } = props;

  const total = useMemo(() => data?.map((d) => d.count).reduce((out, curr) => out + curr, 0) ?? 0, [data]);

  const options = useMemo<AgChartOptions>(
    () => ({
      data,
      title: {
        enabled: !showDemo,
        text: `${toolType === 'Evolve' ? 'EVOLVE' : 'Revit'} Versions for Revit ${selectedRevitYear ?? ''}`,
        textAlign: 'left',
      },
      series: [
        {
          type: 'pie',
          fills: installedVersionsFills ?? createColorScheme(data?.length ?? 0),
          angleKey: 'count',
          calloutLabelKey: 'version',
          calloutLine: {
            length: 35,
          },
          sectorLabelKey: 'count',
          legendItemKey: 'version',
          sectorLabel: {
            formatter: ({ value }) => `${((100 * value) / total).toFixed(2)}%`,
          },
          tooltip: {
            renderer: ({ datum }: AgSeriesTooltipRendererParams<InstalledVersionsData>) => ({
              heading: datum.version,
              data: [
                {
                  label: `${datum.count} users`,
                  value: `(${((100 * datum.count) / total).toFixed(2)}%)`,
                },
              ],
            }),
          },
          title: {
            enabled: showDemo,
            text: `Revit ${selectedRevitYear ?? '--'}`,
          },
        },
      ],
      legend: {
        enabled: !showDemo,
        position: 'right',
        item: {
          marker: {
            shape: 'circle',
          },
        },
      },
    }),
    [data, installedVersionsFills, selectedRevitYear, showDemo, toolType, total],
  );

  return <ChartRenderer options={options} loading={loading} />;
};
